// @ts-strict-ignore
import { Inject, Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { race, throwError, timer } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';

import { updateAbTestingUserConsent, updateConsentSystemInitializationDone } from 'src/app/store/common/common.actions';
import { fromCarl } from 'src/app/store/common/common.selectors';
import { ExecutionMode } from 'src/app/utils/models/execution-mode.enum';
import { WINDOW } from 'src/app/utils/window.token';

import { CurrentHost } from '../../host-identification/current-host.interface';
import { currentHostRef } from '../../host-identification/current-host.token';
import { LoggerService } from '../logger/logger.service';

import { ConsentSystemResponse } from './consent-system-response.model';
import { UsercentricsCmpV2Service, UsercentricsEvent } from './usercentrics-cmp-v2.service';

export interface ConsentConfig {
  isActive: boolean;

  /**
   * CMP v2: Usercentrics Settings ID,
   * see https://confluence.mercedes-benz.io/pages/viewpage.action?spaceKey=DCMP&title=Usercentrics+version+2+solution+Integration
   * see https://confluence.mercedes-benz.io/display/DCMP/SettingsIDs
   */
  v2UsercentricsSettingsId?: string;

  /**
   * CMP v2: Group name. Usually 'Car Configurator A/B-Testing'.
   */
  v2GroupName?: string;
}

interface UsercentricsInitData {
  usercentricsCmpService: UsercentricsCmpV2Service;
  usercentricsSettingsId: string;
  groupName: string;
}

const CONSENT_SYSTEM_RESPONSE_TIMEOUT = 3000;

@Injectable({
  providedIn: 'root'
})
export class ConsentSystemService {
  public constructor(
    private readonly injector: Injector,
    private readonly store: Store,
    private readonly loggerService: LoggerService,
    @Inject(currentHostRef) private readonly currentHost: CurrentHost,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  /**
   * Inits Usercentrics CMP
   * (Consent Management System).
   */
  public init(mode: ExecutionMode): void {
    /**
     * in order to allow local ab testing we additionally need to dispatch a consent loaded event manually
     */
    const dispatchFakeConsentsLoadedEventOnLocalEnv = (consentName: string): void => {
      this.window.document.dispatchEvent(
        new CustomEvent(UsercentricsEvent.ConsentsLoaded, {
          detail: { consents: [{ enabled: true, name: consentName }] }
        })
      );
    };

    this.store
      .pipe(fromCarl.selectConfigOnce<ConsentConfig>('consent'))
      .pipe(filter(consentConfig => !!consentConfig))
      .subscribe((consentConfig: ConsentConfig) => {
        if (consentConfig.isActive) {
          this.initUsercentricsCmp(mode, consentConfig);

          if (this.currentHost.isLocal) {
            dispatchFakeConsentsLoadedEventOnLocalEnv(consentConfig.v2GroupName);
          }
        } else {
          // consent is not active in market at all - neither usercentrics CMP v1 nor v2
          this.updateStore(
            ConsentSystemResponse.createConsentSystemNotInitializedResponse('consent system not activated')
          );
        }
      });
  }

  private initUsercentricsCmp(mode: ExecutionMode, config: ConsentConfig): void {
    const { usercentricsCmpService, usercentricsSettingsId, groupName } = this.getUsercentricsInitData(config);

    if (usercentricsCmpService) {
      race([
        usercentricsCmpService.init(mode, usercentricsSettingsId, groupName),
        timer(CONSENT_SYSTEM_RESPONSE_TIMEOUT).pipe(mergeMap(() => throwError(() => new Error('timeout'))))
      ]).subscribe({
        next: (response: ConsentSystemResponse) => {
          this.updateStore(response);
        },
        error: () => {
          this.updateStore(
            ConsentSystemResponse.createConsentSystemNotInitializedResponse('consent system (usercentrics CMP) timeout')
          );
        }
      });
    } else {
      this.updateStore(ConsentSystemResponse.createConsentSystemNotInitializedResponse('failed looking up instance'));
    }
  }

  private getUsercentricsInitData(config: ConsentConfig): UsercentricsInitData {
    const usercentricsCmpService = this.injector.get<UsercentricsCmpV2Service>(UsercentricsCmpV2Service);
    const usercentricsSettingsId = config.v2UsercentricsSettingsId;
    const groupName = config.v2GroupName;
    return { usercentricsCmpService, usercentricsSettingsId, groupName };
  }

  private updateStore(response: ConsentSystemResponse): void {
    if (response.status === 'not-initialized') {
      this.loggerService.warn(`Consent system not initialized. Reason: ${response.notInitializedReason}`);
    }

    this.storeWhetherUserHasGivenConsentToAbTesting(response.hasAbTestingConsent);
    this.storeThatCmpIsInitialized();
  }

  private storeWhetherUserHasGivenConsentToAbTesting(consentToAbTesting: boolean): void {
    this.store.dispatch(updateAbTestingUserConsent({ hasAbTestingUserConsent: consentToAbTesting }));
  }

  private storeThatCmpIsInitialized(): void {
    this.store.dispatch(updateConsentSystemInitializationDone());
  }
}
